function hexToRgb(hex: string) {
    if (!hex) return null;
    let value = hex.trim();
    if (value.startsWith("rgb")) {
        const match = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (!match) return null;
        return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]) };
    }
    if (value[0] === "#") value = value.slice(1);
    if (value.length === 3) {
        value = value.split("").map((ch) => ch + ch).join("");
    }
    if (value.length !== 6) return null;
    const num = parseInt(value, 16);
    if (Number.isNaN(num)) return null;
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }) {
    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mixWithWhite({ r, g, b }: { r: number; g: number; b: number }, ratio: number) {
    const mix = (channel: number) => Math.round(channel + (255 - channel) * ratio);
    return { r: mix(r), g: mix(g), b: mix(b) };
}

function mixWithBlack({ r, g, b }: { r: number; g: number; b: number }, ratio: number) {
    const mix = (channel: number) => Math.round(channel * (1 - ratio));
    return { r: mix(r), g: mix(g), b: mix(b) };
}

function contrastWithWhite({ r, g, b }: { r: number; g: number; b: number }) {
    const linear = (channel: number) => {
        const c = channel / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
    return 1.05 / (luminance + 0.05);
}

// The tenant color darkened by the given ratio and then until white text on it reads at 4.5:1.
function deepen(rgb: { r: number; g: number; b: number }, ratio: number) {
    let color = mixWithBlack(rgb, ratio);
    while (contrastWithWhite(color) < 4.5) {
        color = { r: Math.floor(color.r * 0.92), g: Math.floor(color.g * 0.92), b: Math.floor(color.b * 0.92) };
    }
    return color;
}

function setBrandTints(color: string | null | undefined) {
    const root = document.documentElement;
    const computed = getComputedStyle(root).getPropertyValue("--main-color");
    const base = (color || computed || "").trim();
    const rgb = hexToRgb(base);
    if (!rgb) {
        if (base) root.style.setProperty("--brand-strong", base);
        root.style.setProperty("--brand-deep", "#2d2244");
        root.style.setProperty("--brand-text", "#2d2244");
        return;
    }
    // A colour almost as light as white (pale greys) would vanish in tints, rings and checks: use its dark version there.
    const accent = contrastWithWhite(rgb) < 1.6 ? deepen(rgb, 0.32) : rgb;
    root.style.setProperty("--brand-strong", rgbToHex(accent));
    root.style.setProperty("--brand-soft", rgbToHex(mixWithWhite(accent, 0.75)));
    root.style.setProperty("--brand-softer", rgbToHex(mixWithWhite(accent, 0.88)));
    root.style.setProperty("--brand-deep", rgbToHex(deepen(rgb, 0.32)));
    root.style.setProperty("--brand-text", rgbToHex(deepen(rgb, 0.38)));
}

function setBrandFont(fontFamily: string | null | undefined) {
    const root = document.documentElement;
    const family = (fontFamily || "").trim();
    if (family) {
        root.style.setProperty("--brand-font", `"${family}", "Urbanist", sans-serif`);
    } else {
        root.style.removeProperty("--brand-font");
    }
}

// Aplica color/fuente/footer/favicon del tenant a las variables CSS globales.
export function applyBrandTheme(shared: any) {
    if (!shared) return;
    const root = document.documentElement;

    if (shared.ButtonsColor !== null && shared.ButtonsColor !== undefined) {
        root.style.setProperty("--main-color", shared.ButtonsColor);
    }
    setBrandTints(shared.ButtonsColor);

    if (shared.FontFamily !== null && shared.FontFamily !== undefined) {
        root.style.setProperty("--main-family", shared.FontFamily);
    }
    setBrandFont(shared.FontFamily);
    if (shared.FooterColor !== null && shared.FooterColor !== undefined) {
        root.style.setProperty("--main-footercolor", shared.FooterColor);
    }

    if (shared.Mainlogo) {
        const logo = shared.Uploads + "/" + shared.Mainlogo;
        let link: any = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
        }
        link.href = logo;
    }
}
